import type { ReactNode } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import type { WaterSource } from "@/lib/api/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  FormField,
  emptyToUndef as _emptyToUndef,
  numToStr,
  parseOptNum as _parseOptNum,
} from "@/components/forms/field";

const optionalNumString = z
  .string()
  .optional()
  .refine(
    (v) => v === undefined || v === "" || !Number.isNaN(Number(v)),
    "must be a number",
  );

export const waterSourceSchema = z.object({
  name: z.string().min(1, "name is required"),
  baseline_ph: optionalNumString,
  baseline_ec: optionalNumString,
  alkalinity_estimate: optionalNumString,
  notes: z.string().optional(),
  reason: z.string().optional(),
});

export type WaterSourceFormValues = z.infer<typeof waterSourceSchema>;

export const parseOptNum = _parseOptNum;
export const emptyToUndef = _emptyToUndef;

export interface WaterSourceFormProps {
  initial?: WaterSource;
  submitting: boolean;
  onSubmit: (values: WaterSourceFormValues) => void;
  banner?: ReactNode;
  submitLabel?: string;
  showReason?: boolean;
}

export function WaterSourceForm({
  initial,
  submitting,
  onSubmit,
  banner,
  submitLabel,
  showReason = false,
}: WaterSourceFormProps) {
  const form = useForm<WaterSourceFormValues>({
    resolver: zodResolver(waterSourceSchema),
    defaultValues: defaultsFor(initial),
  });
  const err = form.formState.errors;
  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      className="space-y-6 max-w-xl"
      noValidate
    >
      {banner}
      <FormField label="Name" error={err.name?.message}>
        <Input autoFocus {...form.register("name")} />
      </FormField>
      <div className="grid grid-cols-3 gap-3">
        <FormField label="Baseline pH" error={err.baseline_ph?.message}>
          <Input type="number" step="any" {...form.register("baseline_ph")} />
        </FormField>
        <FormField label="Baseline EC" error={err.baseline_ec?.message}>
          <Input type="number" step="any" {...form.register("baseline_ec")} />
        </FormField>
        <FormField
          label="Alkalinity"
          help="mg/L CaCO₃"
          error={err.alkalinity_estimate?.message}
        >
          <Input
            type="number"
            step="any"
            {...form.register("alkalinity_estimate")}
          />
        </FormField>
      </div>
      <FormField label="Notes">
        <Textarea rows={3} {...form.register("notes")} />
      </FormField>
      {showReason && (
        <FormField
          label="Reason for change"
          help="Captured in the audit log."
        >
          <Input {...form.register("reason")} />
        </FormField>
      )}
      <Button type="submit" disabled={submitting}>
        {submitting ? "Saving…" : (submitLabel ?? "Save")}
      </Button>
    </form>
  );
}

function defaultsFor(w: WaterSource | undefined): WaterSourceFormValues {
  if (!w) return { name: "", notes: "" };
  return {
    name: w.name,
    baseline_ph: numToStr(w.baseline_ph),
    baseline_ec: numToStr(w.baseline_ec),
    alkalinity_estimate: numToStr(w.alkalinity_estimate),
    notes: w.notes ?? "",
  };
}
